/* eslint-disable max-lines */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Plus, PencilLine, Trash2, X, Loader2, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@components/atoms/ui-kit/button';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import DataGridV2, {
  DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import { StatusBadge, Badge } from '@components/atoms/ui-kit/badge';
import { useGetAdminOrganizationUsers } from '@utils/api/admin/use-get-admin-organization-users';
import { useCreateAdminOrganizationUser } from '@utils/api/admin/use-create-admin-organization-user';
import { useUpdateAdminOrganizationUser } from '@utils/api/admin/use-update-admin-organization-user';
import { useDeleteAdminOrganizationUser } from '@utils/api/admin/use-delete-admin-organization-user';
import { useResetAdminOrganizationUserPassword } from '@utils/api/admin/use-reset-admin-organization-user-password';
import {
  AdminOrganizationUser,
  CreateAdminOrganizationUserPayload,
  UpdateAdminOrganizationUserPayload
} from '@utils/types/admin';

type UserRow = AdminOrganizationUser;

type OrganizationUsersTabProps = {
  organizationId: string;
};

/**
 * Users tab for organization details - full CRUD
 */
export default function OrganizationUsersTab({
  organizationId
}: OrganizationUsersTabProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const {
    data: usersData,
    isLoading,
    isError
  } = useGetAdminOrganizationUsers(organizationId);
  const createMutation = useCreateAdminOrganizationUser();
  const updateMutation = useUpdateAdminOrganizationUser();
  const deleteMutation = useDeleteAdminOrganizationUser();
  const resetPasswordMutation = useResetAdminOrganizationUserPassword();

  const users = usersData?.result ?? [];

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] =
    useState<AdminOrganizationUser | null>(null);

  // Form states for create
  const [createEmail, setCreateEmail] = useState('');
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createIsAdmin, setCreateIsAdmin] = useState(false);
  const [createIsOrgAdmin, setCreateIsOrgAdmin] = useState(false);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  // Form states for edit
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  const [editIsOrgAdmin, setEditIsOrgAdmin] = useState(false);

  const resetCreateForm = () => {
    setCreateEmail('');
    setCreateFirstName('');
    setCreateLastName('');
    setCreateIsAdmin(false);
    setCreateIsOrgAdmin(false);
    setCreateErrors({});
  };

  const resetEditForm = () => {
    setEditFirstName('');
    setEditLastName('');
    setEditIsActive(true);
    setEditIsAdmin(false);
    setEditIsOrgAdmin(false);
  };

  const handleOpenCreate = () => {
    resetCreateForm();
    setIsCreateModalOpen(true);
  };

  const handleOpenEdit = useCallback((user: AdminOrganizationUser) => {
    setSelectedUser(user);
    setEditFirstName(user.first_name);
    setEditLastName(user.last_name);
    setEditIsActive(user.is_active);
    setEditIsAdmin(user.is_admin);
    setEditIsOrgAdmin(user.is_orgadmin);
    setIsEditModalOpen(true);
  }, []);

  const handleOpenDelete = useCallback((user: AdminOrganizationUser) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  }, []);

  const handleOpenResetPassword = useCallback((user: AdminOrganizationUser) => {
    setSelectedUser(user);
    setResetPasswordModalOpen(true);
  }, []);

  const validateCreate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!createEmail.trim()) {
      errors.email = t('email-is-required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createEmail)) {
      errors.email = t('invalid-email');
    }
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = () => {
    if (!validateCreate()) return;

    const payload: CreateAdminOrganizationUserPayload = {
      email: createEmail.trim(),
      first_name: createFirstName.trim(),
      last_name: createLastName.trim(),
      is_admin: createIsAdmin,
      is_orgadmin: createIsOrgAdmin
    };

    createMutation.mutate(
      { organizationId, payload },
      {
        onSuccess: () => {
          setIsCreateModalOpen(false);
          resetCreateForm();
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      }
    );
  };

  const validateEdit = (): boolean => {
    return true;
  };

  const handleUpdate = () => {
    if (!validateEdit() || !selectedUser) return;

    const payload: UpdateAdminOrganizationUserPayload = {
      first_name: editFirstName.trim(),
      last_name: editLastName.trim(),
      is_active: editIsActive,
      is_admin: editIsAdmin,
      is_orgadmin: editIsOrgAdmin
    };

    updateMutation.mutate(
      { organizationId, userId: selectedUser.id, payload },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          resetEditForm();
          setSelectedUser(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      }
    );
  };

  const handleDelete = () => {
    if (!selectedUser) return;

    deleteMutation.mutate(
      { organizationId, userId: selectedUser.id },
      {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedUser(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      }
    );
  };

  const handleResetPassword = () => {
    if (!selectedUser) return;

    resetPasswordMutation.mutate(
      { organizationId, userId: selectedUser.id },
      {
        onSuccess: () => {
          setResetPasswordModalOpen(false);
          setSelectedUser(null);
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: {
              data?: {
                error_description?: string;
                detail?: string;
                message?: string;
              };
            };
          };
          const msg =
            err?.response?.data?.error_description ||
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            t('an-error-occurred');
          toast.error(msg);
        }
      }
    );
  };

  const columns = useMemo<DataGridColumn<UserRow>[]>(
    () => [
      {
        id: 'name',
        header: t('name'),
        field: 'first_name',
        minWidth: 200,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm" style={{ color: getColour('normalText') }}>
            {row.original.first_name} {row.original.last_name}
          </span>
        )
      },
      {
        id: 'email',
        header: t('email'),
        field: 'email',
        minWidth: 250,
        enableSorting: false,
        enableRowCopyOnClick: true,
        cell: ({ row }) => (
          <span
            className="text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            {row.original.email}
          </span>
        )
      },
      {
        id: 'is_admin',
        header: t('admin'),
        field: 'is_admin',
        maxWidth: 100,
        enableSorting: false,
        cell: ({ row }) => (
          <Badge color={row.original.is_admin ? 'blue' : 'zinc'}>
            {row.original.is_admin ? t('yes') : t('no')}
          </Badge>
        )
      },
      {
        id: 'is_orgadmin',
        header: t('org-admin'),
        field: 'is_orgadmin',
        maxWidth: 120,
        enableSorting: false,
        cell: ({ row }) => (
          <Badge color={row.original.is_orgadmin ? 'purple' : 'zinc'}>
            {row.original.is_orgadmin ? t('yes') : t('no')}
          </Badge>
        )
      },
      {
        id: 'is_active',
        header: t('status'),
        field: 'is_active',
        maxWidth: 120,
        enableSorting: false,
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.is_active ? 'active' : 'inactive'}
            label={row.original.is_active ? t('active') : t('inactive')}
          />
        )
      },
      {
        id: 'created_at',
        header: t('created-at'),
        field: 'created_at',
        maxWidth: 150,
        enableSorting: false,
        cell: ({ row }) => (
          <span
            className="text-sm"
            style={{ color: getColour('inactiveText') }}
          >
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        )
      }
    ],
    [t, getColour]
  );

  const actions = useMemo<GridAction<UserRow>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />,
        intent: 'primary',
        run: row => handleOpenEdit(row)
      },
      {
        id: 'reset-password',
        label: t('reset-password'),
        icon: <KeyRound className="h-4 w-4" />,
        run: row => handleOpenResetPassword(row)
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => handleOpenDelete(row)
      }
    ],
    [t, handleOpenEdit, handleOpenResetPassword, handleOpenDelete]
  );

  return (
    <div>
      {/* Header with invite button */}
      <div className="flex justify-between items-center mb-4">
        <h3
          className="text-sm font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {t('users')} ({users.length})
        </h3>
        <Button
          icon={<Plus width={14} />}
          onClick={handleOpenCreate}
          label={t('invite-user')}
          variant="primary"
          size="small"
        />
      </div>

      {isError && (
        <p className="text-sm text-red-500 dark:text-red-400 mb-4">
          {t('an-error-occurred')}
        </p>
      )}
      <DataGridV2<UserRow>
        data={users}
        columns={columns}
        actions={actions}
        enableRowSelection={false}
        enablePagination={false}
        height="auto"
        dense
        striped
        grid={false}
        loading={isLoading}
        getRowId={row => String(row.id)}
      />

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsCreateModalOpen(false)}
          />
          <div
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <div
              className="sticky top-0 flex items-center justify-between p-4 border-b z-10"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('baseLayer')
              }}
            >
              <h2
                className="text-lg font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('invite-user')}
              </h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="p-1 rounded-2xl hover:bg-opacity-80"
                style={{ backgroundColor: getColour('thirdLayer') }}
              >
                <X
                  className="h-5 w-5"
                  style={{ color: getColour('normalText') }}
                />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label
                  className="block text-xs font-medium uppercase tracking-wide mb-2"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('email')} *
                </label>
                <input
                  type="email"
                  value={createEmail}
                  onChange={e => setCreateEmail(e.target.value)}
                  className={`w-full px-3 py-2 rounded-2xl text-sm border ${createErrors.email ? 'border-red-500 dark:border-red-400' : 'border-hairline'}`}
                  style={{
                    backgroundColor: getColour('thirdLayer'),
                    color: getColour('normalText')
                  }}
                  placeholder={t('enter-email')}
                />
                {createErrors.email && (
                  <p className="text-xs mt-1 text-red-500">
                    {createErrors.email}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('first-name')}
                  </label>
                  <input
                    type="text"
                    value={createFirstName}
                    onChange={e => setCreateFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('last-name')}
                  </label>
                  <input
                    type="text"
                    value={createLastName}
                    onChange={e => setCreateLastName(e.target.value)}
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  className="block text-xs font-medium uppercase tracking-wide"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('roles')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createIsAdmin}
                      onChange={e => setCreateIsAdmin(e.target.checked)}
                      className="rounded-2xl"
                    />
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {t('admin')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createIsOrgAdmin}
                      onChange={e => setCreateIsOrgAdmin(e.target.checked)}
                      className="rounded-2xl"
                    />
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {t('org-admin')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div
              className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('baseLayer')
              }}
            >
              <button
                onClick={() => setIsCreateModalOpen(false)}
                disabled={createMutation.isLoading}
                className="px-4 py-2 rounded-2xl text-sm font-medium"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  color: getColour('normalText')
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isLoading}
                className="px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 bg-blue-500 text-white disabled:opacity-50"
              >
                {createMutation.isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {t('invite')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsEditModalOpen(false)}
          />
          <div
            className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl"
            style={{ backgroundColor: getColour('baseLayer') }}
          >
            <div
              className="sticky top-0 flex items-center justify-between p-4 border-b z-10"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('baseLayer')
              }}
            >
              <h2
                className="text-lg font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('edit-user')}
              </h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-1 rounded-2xl hover:bg-opacity-80"
                style={{ backgroundColor: getColour('thirdLayer') }}
              >
                <X
                  className="h-5 w-5"
                  style={{ color: getColour('normalText') }}
                />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label
                  className="block text-xs font-medium uppercase tracking-wide mb-2"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={selectedUser.email}
                  disabled
                  className="w-full px-3 py-2 rounded-2xl text-sm opacity-50"
                  style={{
                    backgroundColor: getColour('thirdLayer'),
                    border: `1px solid ${getColour('border')}`,
                    color: getColour('normalText')
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('first-name')}
                  </label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={e => setEditFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                  />
                </div>
                <div>
                  <label
                    className="block text-xs font-medium uppercase tracking-wide mb-2"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('last-name')}
                  </label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={e => setEditLastName(e.target.value)}
                    className="w-full px-3 py-2 rounded-2xl text-sm"
                    style={{
                      backgroundColor: getColour('thirdLayer'),
                      border: `1px solid ${getColour('border')}`,
                      color: getColour('normalText')
                    }}
                  />
                </div>
              </div>

              <div
                className="rounded-2xl border p-4"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('thirdLayer')
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <label
                      className="text-sm font-medium"
                      style={{ color: getColour('normalText') }}
                    >
                      {t('active')}
                    </label>
                    <p
                      className="text-xs"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('user-active-description')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditIsActive(!editIsActive)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editIsActive ? 'bg-green-500' : ''}`}
                    style={
                      editIsActive
                        ? undefined
                        : { backgroundColor: getColour('border') }
                    }
                  >
                    <span
                      // eslint-disable-next-line no-restricted-syntax
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        editIsActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  className="block text-xs font-medium uppercase tracking-wide"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('roles')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editIsAdmin}
                      onChange={e => setEditIsAdmin(e.target.checked)}
                      className="rounded-2xl"
                    />
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {t('admin')}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editIsOrgAdmin}
                      onChange={e => setEditIsOrgAdmin(e.target.checked)}
                      className="rounded-2xl"
                    />
                    <span
                      className="text-sm"
                      style={{ color: getColour('normalText') }}
                    >
                      {t('org-admin')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div
              className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('baseLayer')
              }}
            >
              <button
                onClick={() => setIsEditModalOpen(false)}
                disabled={updateMutation.isLoading}
                className="px-4 py-2 rounded-2xl text-sm font-medium"
                style={{
                  backgroundColor: getColour('thirdLayer'),
                  color: getColour('normalText')
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateMutation.isLoading}
                className="px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 bg-blue-500 text-white disabled:opacity-50"
              >
                {updateMutation.isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {t('save-changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleDelete}
        size="small"
        title={t('remove-user')}
        caption={t('are-you-sure-you-want-to-remove-this-user')}
        confirmLabel={deleteMutation.isLoading ? t('removing') : t('remove')}
        cancelLabel={t('cancel')}
        confirmDisabled={deleteMutation.isLoading}
        destructive
        autoFocusConfirm
      />

      {/* Reset Password Confirmation Modal */}
      <ConfirmationModal
        isOpen={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleResetPassword}
        size="small"
        title={t('reset-password')}
        caption={t('reset-password-confirmation', {
          email: selectedUser?.email
        })}
        confirmLabel={
          resetPasswordMutation.isLoading ? t('sending') : t('send-reset-link')
        }
        cancelLabel={t('cancel')}
        confirmDisabled={resetPasswordMutation.isLoading}
        autoFocusConfirm
      />
    </div>
  );
}
